import type { ServicePageConfig } from "../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../package-pricing";

export const bortforslingPages: ServicePageConfig[] = [
  {
    // Bortforsling — huvudsida /bortforsling
    slug: "bortforsling",
    serviceCategory: "bortforsling",

    meta: {
      title: "Bortforsling – Snabb & Smidig | Ungdomskraft",
      description:
        "Boka bortforsling i Stockholm och Göteborg. Vi hämtar möbler, vitvaror och skräp – fast pris, försäkrat och med unga medarbetare. 50 000+ uppdrag sedan 2014.",
      h1: "Bortforsling",
    },

    hero: {
      heading: "Hjälp med bortforsling",
      intro:
        "Boka bortforsling av möbler, vitvaror och skrymmande föremål – snabbt och enkelt. Vi skickar ungdomar med lastbil direkt till din adress, ofta samma dag. Fast pris, enkel bokning och över 50 000 slutförda uppdrag.",
      heroImage: "/hero-bortforsling.webp",
    },

    itemsSection: {
      heading: "Saker vi bortforslar",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling",
      mainText:
        "Att bli av med skrymmande möbler, elektronik eller ett helt bohag behöver inte vara krångligt. Med Ungdomskraft får du snabb och smidig bortforsling – oavsett om det handlar om en enstaka soffa, ett helt dödsbo eller ett förråd som behöver tömmas. Vi tar hand om allt från tunga lyft till ansvarsfull sortering och återvinning, så att du slipper köra till tippen själv.\n\nVåra unga, energiska medarbetare hämtar ditt skräp direkt från hemmet, kontoret eller förrådet. Du visar bara vad som ska bort – vi bär, lastar och kör iväg allt åt dig.",
      preparationHeading: "Förbered inför din bortforsling",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen kan du rensa en fri väg (ca 1 meter bred) från entrén till föremålen och om möjligt samla sakerna på ett ställe. Det minskar arbetstiden och håller nere kostnaden.",
      additionalSections: [
        {
          heading: "Varför boka bortforsling med Ungdomskraft?",
          listItems: [
            {
              label: "Enkel bokning utan krångel –",
              text: "Boka din bortforsling på några minuter. Fyll i adress, välj datum och få pris direkt – slipp väntan på offert. Behöver du hjälp redan idag? Vi kan vara på plats redan inom 60 minuter för akuta behov. Självklart kan du även ringa in och boka på 08-517 082 81.",
            },
            {
              label: "Flexibla tider som passar dig –",
              text: "Vi vet att din tid är värdefull. Därför erbjuder vi bortforsling på tider som passar ditt schema – vardagar, kvällar och helger. Välj det datum och den tid som fungerar bäst för dig.",
            },
            {
              label: "Miljövänlig hantering och återvinning –",
              text: "Vi sorterar och återvinner så mycket som möjligt. Det som kan doneras skänks vidare till organisationer som Myrorna och Stadsmissionen. Resten hanteras ansvarsfullt och körs till godkänd återvinningsstation. Du kan även ha rätt till RUT-avdrag när vi transporterar saker till återanvändning.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Du kan känna dig trygg med att dina ägodelar och din fastighet är skyddade under hela processen – från lyft till transport.",
            },
          ],
        },
        {
          heading: "Vad kan vi forsla bort?",
          content:
            "Vi hanterar de flesta typer av skrymmande föremål och avfall. Oavsett om du behöver bli av med enstaka saker eller tömma ett helt utrymme hjälper vi dig:",
          listItems: [
            {
              label: "Möbler –",
              text: "soffor, sängar, bord, stolar, bokhyllor och garderober",
            },
            {
              label: "Vitvaror –",
              text: "tvättmaskiner, torktumlare, kylskåp, frysar och spisar",
            },
            {
              label: "Elektronik –",
              text: "TV-apparater, datorer, skärmar och mindre elavfall",
            },
            {
              label: "Grovsopor –",
              text: "madrasser, mattor, lampor och annat som inte ryms i vanliga sopkärlet",
            },
            {
              label: "Trädgårdsavfall –",
              text: "grenar, jord, sten och övrigt trädgårdsskräp",
            },
            {
              label: "Byggavfall –",
              text: "rivningsmaterial, gipsskivor och skräp efter mindre renoveringar",
            },
            {
              label: "Kontorsinventarier –",
              text: "skrivbord, kontorsstolar, arkivskåp och IT-utrustning",
            },
          ],
          afterContent:
            "Vi tar även emot blandade lass där allt möjligt ska bort samtidigt. Är du osäker på om vi kan hantera just ditt avfall? Hör av dig så hjälper vi dig.",
        },
        {
          heading: "Så fungerar det – bortforsling i tre enkla steg",
          listItems: [
            {
              label: "1. Beskriv ditt behov –",
              text: "Fyll i vårt bokningsformulär med adress, vad som ska forslas bort och önskat datum. Du får ett pris direkt – inga dolda kostnader.",
            },
            {
              label: "2. Vi kommer till dig –",
              text: "Våra ungdomar dyker upp på överenskommen tid, bär ut allt och lastar på bilen. Vi hanterar trapphus, hissar och trånga utrymmen – du behöver inte lyfta ett finger.",
            },
            {
              label: "3. Ansvarsfull hantering –",
              text: "Vi kör ditt avfall till rätt ställe. Det som går att återanvända skänks vidare, resten sorteras och lämnas på godkänd återvinningsstation. Allt hanteras enligt gällande regler så att så lite som möjligt hamnar på deponi.",
            },
          ],
        },
        {
          heading: "Erfarna bortforslare med lokalkännedom",
          content:
            "Våra ungdomar har gedigen erfarenhet av bortforsling. De är vana vid trånga trapphus, smala hissar och innergårdar som kräver extra planering. Oavsett om du bor i en villa, en lägenhet eller ett radhus vet de hur uppdraget ska genomföras på ett effektivt och säkert sätt.\n\nVi utför dagligen bortforsling av allt från enstaka möbler till kompletta lägenhetsröjningar och dödsbon. Den erfarenheten gör att vi kan ge dig ett exakt pris redan vid bokning och genomföra uppdraget utan fördröjningar.",
        },
        {
          heading: "Boka bortforsling idag",
          content:
            "Letar du efter bortforsling nära mig och vill ha en tjänst som kombinerar smidighet, pålitlighet och bra pris? Då är Ungdomskraft rätt val. Vi gör det enkelt att bli av med det du inte längre behöver – snabbt, tryggt och miljövänligt.\n\nFyll i formuläret så får du pris direkt. Inga offertförfrågningar – bara enkel bortforsling som dessutom gör skillnad. Självklart kan du även ringa in och boka bortforsling på 08-517 082 81.",
          cta: { text: "Boka bortforsling", href: "#priser" },
        },
      ],
    },

    faq: [
      {
        question: "Vilka typer av föremål kan ni hantera vid bortforsling?",
        answer:
          "Vi kan forsla bort nästan allt – från gamla möbler, madrasser och vitvaror till kartonger, trädgårdsavfall och allmänt hushållsskräp. Vi tar även skrymmande föremål som soffor, bokhyllor och träningsutrustning. Oavsett om det gäller en garageröjning, kontorsröjning eller en hel lägenhetsröjning gör våra ungdomar bortforslingen snabb och smidig. Av säkerhetsskäl kan vi inte hantera farligt avfall som kemikalier, färg eller asbest.",
      },
      {
        question:
          "Kan ni bortforsla saker direkt från mitt hem (inomhus), eller måste de stå utomhus?",
        answer:
          "Ja, våra ungdomar hämtar dina saker var de än står – inne i bostaden, på vinden, i källaren eller i garaget. Du behöver inte bära ut något själv. Vi är vana vid trånga trapphus, smala hissar och svåråtkomliga utrymmen.",
      },
      {
        question: "Vad händer med mina saker efter bortforslingen?",
        answer:
          "Vi sorterar allt vi forslar bort. Det som fortfarande är i bra skick skänks vidare till organisationer som Myrorna eller Stadsmissionen. Resten lämnas på godkänd återvinningsstation där materialet sorteras och återvinns enligt gällande miljöregler. Vårt mål är alltid att så lite som möjligt hamnar på deponi.",
      },
      {
        question:
          "Kan ni bortforsla madrasser, elektronik eller vitvaror?",
        answer:
          "Absolut. Vi hämtar och forslar bort madrasser, TV-apparater, datorer, tvättmaskiner, kylskåp, frysar och andra vitvaror. Elektronik och vitvaror sorteras ut separat och lämnas till godkänd elektronikåtervinning. Eventuella avfallsavgifter på återvinningsstationen kan tillkomma beroende på föremålstyp.",
      },
      {
        question:
          "Hur ska jag förbereda mig inför min bortforsling?",
        answer:
          "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen kan du rensa en fri väg (ca 1 meter bred) från entrén till föremålen och om möjligt samla sakerna på ett ställe. Det minskar arbetstiden och håller nere kostnaden.",
      },
      {
        question:
          "Erbjuder ni bortforsling samma dag eller nästa dag?",
        answer:
          "Ja, vi erbjuder både samdags- och nästadagsservice. Du kan boka våra ungdomar med så kort varsel som 30 minuter eller planera upp till 30 dagar i förväg. Välj helt enkelt den tid som passar dig bäst i bokningsformuläret.",
      },
      {
        question:
          "Kan ni montera isär möbler vid bortforsling?",
        answer:
          "Ja, våra ungdomar hjälper gärna till med enklare isärmontering i samband med din bortforsling. De har med sig standardverktyg, men om dina möbler kräver specialverktyg eller instruktioner ber vi dig ha dessa redo. Tid för isärmontering ingår i din totala arbetstid.",
      },
      {
        question:
          "Kan ni skänka mina saker istället för att bortforsla dem?",
        answer:
          "Ja! Om dina saker fortfarande är i bra skick kan vi leverera dem till en secondhand-butik eller välgörenhetsorganisation istället för att köra dem till tippen. Ange bara önskad inlämningsplats vid bokning. Vi rekommenderar att du kontaktar mottagaren i förväg för att bekräfta att de kan ta emot dina saker. Transport till återanvändning kan dessutom ge dig rätt till RUT-avdrag (25 %).",
      },
      {
        question:
          "Ingår avfallsavgifter i priset för min bortforsling?",
        answer:
          "Nej, avfalls- och deponiavgifter ingår inte i priset utan debiteras separat. Avgiften varierar beroende på återvinningsstation samt vikten och typen av föremål, men ligger oftast runt 400 kr. Du ser alltid den totala kostnaden innan vi påbörjar uppdraget, så att du vet exakt vad det kostar.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling?",
        answer:
          "Det beror på vad som händer med sakerna. Om dina föremål transporteras till en secondhand-butik, välgörenhetsorganisation eller annan plats för återanvändning har du rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där sakerna körs direkt till återvinningsstation eller deponi ger däremot inte rätt till RUT-avdrag enligt Skatteverkets regler. Vi hjälper dig gärna att avgöra vad som gäller för just ditt uppdrag – ange gärna vid bokning om du vill att sakerna ska skänkas så planerar vi upplägget därefter. Avdraget hanteras direkt på fakturan, så du behöver aldrig ligga ute med pengarna.",
      },
      {
        question: "Vad kostar bortforsling?",
        answer:
          "Priset beror på mängden, typen av föremål och hur lång tid uppdraget tar. Du får ett pris direkt i vår bokning baserat på vad du anger – inga dolda avgifter eller överraskningar i efterhand. Avfallsavgifter på återvinningsstationen tillkommer separat och ligger oftast runt 400 kr. Vill du ha en exakt uppskattning för ett större uppdrag? Kontakta oss så återkommer vi med en skräddarsydd offert.",
      },
      {
        question: "Hur lång tid tar en bortforsling?",
        answer:
          "Det varierar beroende på mängden och hur tillgängliga föremålen är. En enklare bortforsling av ett par möbler tar vanligtvis 30–60 minuter. Större uppdrag som en lägenhetsröjning eller dödsbotömning kan ta en halv till en hel dag. Våra ungdomar jobbar effektivt och är vana vid att bära tungt – vi ser till att det går så snabbt som möjligt utan att tumma på kvaliteten.",
      },
      {
        question:
          "Kan ni hjälpa med dödsbo eller lägenhetsröjning?",
        answer:
          "Ja, vi har gedigen erfarenhet av att tömma dödsbon och hela lägenheter. Våra ungdomar hanterar allt från möbler och personliga tillhörigheter till grovsopor och elektronik. Vi sorterar noggrant – det som fortfarande är i bra skick skänks vidare och resten lämnas på godkänd återvinningsstation. Du behöver inte vara på plats om du inte vill, vi sköter hela processen åt dig.",
      },
      {
        question:
          "Vilka områden erbjuder ni bortforsling i?",
        answer:
          "Vi erbjuder bortforsling i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö. Hittar du inte ditt område? Hör av dig så berättar vi om vi kan hjälpa dig.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i 3 enkla steg",
      step2Text:
        "Våra ungdomar kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus och trånga hissar – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Enkel bortforsling som gör skillnad.",
    },

    en: {
      slug: "junk-removal",

      meta: {
        title: "Junk Removal | Ungdomskraft",
        description:
          "Book junk removal in Stockholm and Gothenburg. We collect furniture, appliances and bulky items – fixed price, insured and 50,000+ jobs since 2014. Book today!",
        h1: "Junk Removal",
      },

      hero: {
        heading: "Help with junk removal",
        intro:
          "Book junk removal of furniture, appliances, and bulky items – fast and easy. We send young workers with a truck straight to your address, often the same day. Fixed price, simple booking, and over 50,000 completed jobs.",
      },

      itemsSection: {
        heading: "Junk we remove",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper, and other recyclables",
            image: "/recycling-bin.webp",
          },
          {
            name: "Cardboard",
            description: "Moving boxes and corrugated cardboard",
            image: "/box.webp",
          },
          {
            name: "Furniture",
            description:
              "Living room, bedroom, office, and other household furniture",
            image: "/furniture.webp",
          },
          {
            name: "Electronics",
            description:
              "TVs, computers, audio systems, and other electronics",
            image: "/electronics.webp",
          },
          {
            name: "Appliances",
            description:
              "Refrigerators, washing machines, dryers, and other appliances",
            image: "/appliances.webp",
          },
          {
            name: "Fitness Equipment",
            description:
              "Treadmills, bikes, weights, and other workout equipment",
            image: "/fitness-equipment.webp",
          },
        ],
      },

      seoContent: {
        mainHeading: "Junk Removal",
        mainText:
          "Getting rid of bulky furniture, electronics, or an entire household doesn't have to be complicated. With Ungdomskraft you get fast, hassle-free junk removal – whether it's a single sofa, a full estate clearance, or a storage unit that needs emptying. We take care of everything from heavy lifting to responsible sorting and recycling, so you never have to drive to the dump yourself.\n\nOur young, energetic workers pick up your junk directly from your home, office, or storage unit. Just point at what needs to go – we carry, load, and haul it all away for you.",
        preparationHeading: "Prepare for your junk removal",
        preparationText:
          "You really don't need to do much at all – our workers handle the heavy lifting. But if you want to speed things up, you can clear a path (about 1 metre wide) from the entrance to the items, and if possible, gather them in one place. This reduces work time and keeps the cost down.",
        additionalSections: [
          {
            heading: "Why book junk removal with Ungdomskraft?",
            listItems: [
              {
                label: "Simple booking, no hassle –",
                text: "Book your junk removal in minutes. Enter your address, pick a date, and get an instant price – no waiting for quotes. Need help today? We can be there within 60 minutes for urgent requests. You can also call us at +46 8-517 082 81.",
              },
              {
                label: "Flexible times that suit you –",
                text: "We know your time is valuable. That's why we offer junk removal at times that fit your schedule – weekdays, evenings, and weekends. Choose the date and time that works best for you.",
              },
              {
                label: "Eco-friendly handling and recycling –",
                text: "We sort and recycle as much as possible. Items in good condition are donated to charity organisations. The rest is handled responsibly and taken to approved recycling stations. You may also be eligible for the Swedish RUT tax deduction when we transport items for reuse.",
              },
              {
                label: "Insured and secure –",
                text: "Every job is insured through Trygg-Hansa. You can feel confident that your belongings and property are protected throughout the entire process – from lifting to transport.",
              },
            ],
          },
          {
            heading: "What junk can we remove?",
            content:
              "We handle most types of bulky items and waste. Whether you need to get rid of a few things or clear out an entire space, we're here to help:",
            listItems: [
              {
                label: "Furniture –",
                text: "sofas, beds, tables, chairs, bookshelves, and wardrobes",
              },
              {
                label: "Appliances –",
                text: "washing machines, dryers, refrigerators, freezers, and stoves",
              },
              {
                label: "Electronics –",
                text: "TVs, computers, monitors, and smaller e-waste",
              },
              {
                label: "Bulk waste –",
                text: "mattresses, rugs, lamps, and other items that don't fit in regular bins",
              },
              {
                label: "Garden waste –",
                text: "branches, soil, stones, and other garden debris",
              },
              {
                label: "Construction debris –",
                text: "demolition materials, drywall, and waste from minor renovations",
              },
              {
                label: "Office equipment –",
                text: "desks, office chairs, filing cabinets, and IT equipment",
              },
            ],
            afterContent:
              "We also accept mixed loads where everything needs to go at once. Not sure if we can handle your particular waste? Get in touch and we'll help you out.",
          },
          {
            heading: "How it works – junk removal in three easy steps",
            listItems: [
              {
                label: "1. Describe what you need –",
                text: "Fill in our booking form with your address, what needs to be removed, and your preferred date. You'll get an instant price – no hidden costs.",
              },
              {
                label: "2. We come to you –",
                text: "Our young workers show up at the agreed time, carry everything out, and load up the truck. We handle staircases, elevators, and tight spaces – you don't have to lift a finger.",
              },
              {
                label: "3. Responsible disposal –",
                text: "We take your waste to the right place. Items that can be reused are donated; the rest is sorted and delivered to an approved recycling station. Everything is handled according to regulations so as little as possible ends up in landfill.",
              },
            ],
          },
          {
            heading: "Experienced junk haulers with local knowledge",
            content:
              "Our young workers have extensive experience with junk removal. They are used to tight staircases, narrow elevators, and courtyards that require extra planning. Whether you live in a house, an apartment, or a townhouse, they know how to get the job done efficiently and safely.\n\nWe carry out daily junk removals – from single pieces of furniture to complete apartment clearances and estate clear-outs. That experience means we can give you an accurate price at the time of booking and complete the job without delays.",
          },
          {
            heading: "Book junk removal today",
            content:
              "Looking for junk removal near me and want a service that combines convenience, reliability, and great value? Then Ungdomskraft is the right choice. We make it easy to get rid of what you no longer need – fast, safe, and eco-friendly.\n\nFill in the form and get an instant price. No quote requests – just simple junk removal that also makes a difference. You can also call us to book at +46 8-517 082 81.",
            cta: { text: "Book junk removal", href: "#priser" },
          },
        ],
      },

      faq: [
        {
          question: "What types of items can you haul away?",
          answer:
            "We can remove almost anything – from old furniture, mattresses, and appliances to cardboard boxes, garden waste, and general household junk. We also handle bulky items like sofas, bookshelves, and fitness equipment. Whether it's a garage cleanout, office clearance, or a full apartment clear-out, our young workers make the junk removal quick and easy. For safety reasons, we cannot handle hazardous waste such as chemicals, paint, or asbestos.",
        },
        {
          question:
            "Can you pick up items from inside my home, or do they need to be outside?",
          answer:
            "Yes, our workers will pick up your items wherever they are – inside your home, in the attic, basement, or garage. You don't need to carry anything out yourself. We're experienced with tight staircases, narrow elevators, and hard-to-reach spaces.",
        },
        {
          question:
            "What happens to my items after the junk removal?",
          answer:
            "We sort everything we haul away. Items still in good condition are donated to charity organisations. The rest is taken to an approved recycling station where materials are sorted and recycled according to environmental regulations. Our goal is always to send as little as possible to landfill.",
        },
        {
          question:
            "Can you remove mattresses, electronics, or appliances?",
          answer:
            "Absolutely. We pick up and haul away mattresses, TVs, computers, washing machines, refrigerators, freezers, and other appliances. Electronics and appliances are separated and taken to approved e-waste recycling. Additional disposal fees at the recycling station may apply depending on the item type.",
        },
        {
          question:
            "How should I prepare for my junk removal?",
          answer:
            "You really don't need to do much at all – our workers handle the heavy lifting. But if you want to speed things up, you can clear a path (about 1 metre wide) from the entrance to the items, and if possible, gather them in one place. This reduces work time and keeps the cost down.",
        },
        {
          question:
            "Do you offer same-day or next-day junk removal?",
          answer:
            "Yes, we offer both same-day and next-day service. You can book our workers with as little as 30 minutes' notice, or plan up to 30 days in advance. Simply choose the time that works best for you in the booking form.",
        },
        {
          question:
            "Can you disassemble furniture during the junk removal?",
          answer:
            "Yes, our workers are happy to help with basic disassembly as part of your junk removal. They carry standard tools, but if your furniture requires special tools or instructions, please have them ready. Time for disassembly is included in your total work time.",
        },
        {
          question:
            "Can you donate my items instead of hauling them to the dump?",
          answer:
            "Yes! If your items are still in good condition, we can deliver them to a secondhand store or charity organisation instead of the recycling station. Just specify the preferred drop-off location when booking. We recommend contacting the recipient in advance to confirm they can accept your items. Transport for reuse may also qualify you for the Swedish RUT tax deduction (25%).",
        },
        {
          question: "Are disposal fees included in the price?",
          answer:
            "No, waste and disposal fees are not included in the price and are charged separately. The fee varies depending on the recycling station and the weight and type of items, but is usually around 400 kr. You'll always see the total cost before we start the job, so you know exactly what to expect.",
        },
        {
          question:
            "Can I get a RUT tax deduction on junk removal?",
          answer:
            "It depends on what happens to the items. If your items are transported to a secondhand shop, charity, or another location for reuse, you're eligible for the RUT tax deduction on the labour cost. Regular junk removal where items go directly to a recycling station or landfill does not qualify for the RUT deduction under Swedish tax rules. We're happy to help you figure out what applies to your specific job – just let us know at booking if you'd like items donated and we'll plan accordingly. The deduction is applied directly on the invoice, so you never have to front the money.",
        },
        {
          question: "How much does junk removal cost?",
          answer:
            "The price depends on the volume, type of items, and how long the job takes. You get a price instantly in our booking system based on what you enter – no hidden fees or surprises. Disposal fees at the recycling station are charged separately and usually run around 400 kr. Want a precise estimate for a larger job? Contact us and we'll get back to you with a tailored quote.",
        },
        {
          question: "How long does a junk removal take?",
          answer:
            "It varies depending on the volume and how accessible the items are. A simple removal of a couple of furniture pieces usually takes 30–60 minutes. Larger jobs like an apartment clearance or estate clear-out can take half a day to a full day. Our young workers are efficient and used to heavy lifting – we make sure it goes as quickly as possible without compromising quality.",
        },
        {
          question:
            "Can you help with estate clearance or apartment clear-outs?",
          answer:
            "Yes, we have extensive experience clearing estates and entire apartments. Our workers handle everything from furniture and personal belongings to bulk waste and electronics. We sort carefully – items still in good condition are donated and the rest is taken to an approved recycling station. You don't need to be present if you don't want to – we'll manage the entire process for you.",
        },
        {
          question:
            "Which areas do you offer junk removal in?",
          answer:
            "We offer junk removal across the entire Stockholm area: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö, and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn, and Öckerö. Don't see your area? Get in touch and we'll let you know if we can help.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,

      pricingRows: getPackagePricingRows("bortforsling", "en"),

      dynamicHeadings: {
        threeSteps: "Book your junk removal in 3 easy steps",
        step2Text:
          "Our young workers come to you and collect whatever you want removed. We handle heavy lifting, tight staircases, and narrow elevators – you don't have to lift a finger.",
        ctaHeading: "Book junk removal today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Simple junk removal that makes a difference.",
      },
    },
  },
  {
    // Bortforsling möbler — config för /bortforsling-mobler
    // Vinkel: Bred möbelsida — alla typer av möbler, övergripande.
    // Nämner specifika kategorier (soffa, säng) som naturliga interna länkar nedåt.
    slug: "bortforsling-mobler",
    serviceCategory: "bortforsling",

    meta: {
      title: "Bortforsling möbler | Ungdomskraft",
      description:
        "Boka bortforsling av möbler i Stockholm och Göteborg. Vi bär ut soffor, sängar, bord och garderober. Fast pris, försäkrat och med unga medarbetare. Boka idag!",
      h1: "Bortforsling möbler",
    },

    hero: {
      heading: "Bortforsling av möbler",
      intro:
        "Dags att bli av med gamla möbler? Vi hämtar allt från en enstaka fåtölj till ett helt hem fullt med bohag. Våra ungdomar bär ut, monterar isär det som behövs och kör allt till återvinning. Du visar bara vad som ska bort.",
      heroImage: "/hero-bortforsling.webp",
    },

    itemsSection: {
      heading: "Möbler vi hämtar",
      items: [
        {
          name: "Soffor",
          description: "Soffgrupper, 2-sitssoffor och divansoffor",
          image: "/sofa.webp",
        },
        {
          name: "Stolar",
          description:
            "Fåtöljer, accent- och läsfåtöljer, gungstolar och matstolar",
          image: "/armchair.webp",
        },
        {
          name: "Sängar",
          description: "Sängstommar, madrasser och sänggavlar",
          image: "/bed.webp",
        },
        {
          name: "Bord",
          description: "Matbord, köksbord, soffbord och sängbord",
          image: "/table.webp",
        },
        {
          name: "Förvaring",
          description: "Byråer, garderober och förvaringsskap",
          image: "/wardrobe.webp",
        },
        {
          name: "Utemöbler",
          description: "Trädgårdsmöbler och utemöbler",
          image: "/patio-furniture.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Smidig bortforsling av möbler",
      mainText:
        "Gamla möbler är bland det krångligaste att bli av med själv. De är tunga, skrymmande och passar sällan i en vanlig bil. Dessutom kräver garderober och sängar ofta isärplockning innan de ens går att få ut genom dörren. Med Ungdomskraft slipper du allt det.\n\nVi skickar ungdomar med rätt fordon direkt till dig. De monterar isär det som behövs, bär ut allt genom trapphus och trånga passager och lastar på bilen. Saker i gott skick skänks vidare till organisationer som Myrorna och Stadsmissionen — resten körs till godkänd återvinningsstation.\n\nDet spelar ingen roll om du ska byta ut en enstaka soffa eller tömma ett helt rum. Boka online och välj en tid som passar dig, så sköter vi resten.",
      preparationHeading: "Förbered inför möbelhämtningen",
      preparationText:
        "Du behöver inte montera isär någonting — det fixar våra ungdomar på plats. Däremot är det bra att tömma lådor och hyllor innan vi kommer, så går det snabbare. Flytta gärna undan saker som står i vägen mellan möblerna och ytterdörren. Bor du i lägenhet, kontrollera om det finns hiss — och om möblerna får plats i den. Har du möbler du vill skänka separat? Säg till så hjälper vi dig sortera på plats.",
      additionalSections: [
        {
          heading: "Varför boka bortforsling av möbler med Ungdomskraft?",
          listItems: [
            {
              label: "Enkel bokning utan krångel –",
              text: "Boka din möbelbortforsling på några minuter. Fyll i adress, välj datum och få pris direkt – slipp väntan på offert. Behöver du hjälp redan idag? Vi kan vara på plats redan inom 60 minuter. Du kan även ringa oss på 08-517 082 81.",
            },
            {
              label: "Vi bär ut allt åt dig –",
              text: "Du behöver inte lyfta ett finger. Våra ungdomar bär ut möblerna från vilken våning och genom vilka utrymmen som helst – trånga trapphus, smala hissar och innergårdar är inga problem.",
            },
            {
              label: "Möbler skänks eller återvinns –",
              text: "Möbler i gott skick skänks vidare till organisationer som Myrorna och Stadsmissionen. Resten sorteras och lämnas på godkänd återvinningsstation. Du kan även ha rätt till RUT-avdrag när möbler transporteras till återanvändning.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg-Hansa. Din bostad och dina ägodelar är skyddade under hela processen – från lyft till transport.",
            },
          ],
        },
        {
          heading: "Vilka möbler kan vi forsla bort?",
          content:
            "Vi hämtar och forslar bort alla typer av möbler, oavsett storlek och skick:",
          listItems: [
            {
              label: "Soffor & fåtöljer –",
              text: "soffgrupper, bäddsoffor, divaner, recliners och fåtöljer",
            },
            {
              label: "Sängar –",
              text: "sängstommar, madrasser, resårbottnar, sänggavlar och våningssängar",
            },
            {
              label: "Bord & stolar –",
              text: "matbord, soffbord, skrivbord, stolar och barstolar",
            },
            {
              label: "Förvaring –",
              text: "bokhyllor, garderober, byråer, kommoder, skåp och hyllsystem",
            },
            {
              label: "Kontorsmöbler –",
              text: "skrivbord, kontorsstolar, arkivskåp, konferensbord och receptionsdiskar",
            },
            {
              label: "Övriga möbler –",
              text: "TV-möbler, skoställ, speglar, hatthyllor och trädgårdsmöbler",
            },
          ],
          afterContent:
            "Vi monterar även isär möbler som behöver demonteras innan de kan bäras ut. Är du osäker på om vi kan hantera just dina möbler? Hör av dig så hjälper vi dig.",
        },
        {
          heading: "Erfarna möbelbärare med lokalkännedom",
          content:
            "Våra ungdomar har gedigen erfarenhet av att bära och forsla bort möbler. De vet hur man manövrerar en stor soffa genom ett trångt trapphus, hur man skyddar väggar och golv vid tunga lyft, och hur man lastar effektivt för att minimera antalet turer.\n\nVi utför dagligen möbelbortforsling av allt från enstaka fåtöljer till kompletta bostadstömningar. Den erfarenheten gör att vi kan ge dig ett exakt pris redan vid bokning och genomföra uppdraget smidigt och utan fördröjningar.",
        },
        {
          heading: "Mer än bara bortforsling av möbler – du gör skillnad",
          content:
            "Det som gör Ungdomskraft unikt är att vi sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet. När du bokar bortforsling av möbler hos oss får du inte bara professionell hjälp – du bidrar också till att unga människor kommer ut i arbetslivet och bygger sin framtid.\n\nVarje uppdrag bemannas av motiverade ungdomar som får lära sig allt från kundkontakt och logistik till fysiskt arbete och ansvarsfullt miljöarbete. Det är möbelbortforsling som gör skillnad.",
        },
      ],
    },

    faq: [
      {
        question: "Kan ni montera isär möbler innan ni forslar bort dem?",
        answer:
          "Ja, våra ungdomar monterar isär det som behövs — garderober, sängramar, skrivbord och liknande. De har med sig standardverktyg. Om möblerna kräver specialverktyg eller monteringsinstruktioner ber vi dig ha dessa redo. Tid för isärmontering ingår i din totala arbetstid.",
      },
      {
        question: "Klarar ni tunga möbler i trapphus utan hiss?",
        answer:
          "Absolut. Våra ungdomar är vana vid tunga lyft i trånga trapphus, smala hissar och svåråtkomliga utrymmen. Vi hanterar soffor, garderober och sängar oavsett våningsplan. Du behöver inte bära ut något själv.",
      },
      {
        question:
          "Kan ni ta med alla möbler i ett rum, eller måste jag boka separat?",
        answer:
          "Du kan boka ett uppdrag för hur många möbler som helst. Ange ungefär vad det rör sig om så skickar vi rätt fordon och antal personer. Allt från en enstaka fåtölj till ett helt bohag går att hantera på samma bokning.",
      },
      {
        question: "Vad händer med möblerna efteråt?",
        answer:
          "Möbler i gott skick skänks vidare till organisationer som Myrorna eller Stadsmissionen. Trasiga eller slitna möbler lämnas på godkänd återvinningsstation där materialet — trä, metall, textil — sorteras och återvinns. Vårt mål är att så lite som möjligt hamnar på deponi.",
      },
      {
        question: "Hur snabbt kan ni komma?",
        answer:
          "Vi kan ofta vara på plats samma dag. Du kan boka med så kort varsel som 30 minuter eller planera upp till 30 dagar i förväg. Välj den tid som passar dig bäst — du ser tillgängliga tider direkt vid bokning.",
      },
      {
        question: "Tar ni även med småsaker som hör till möblerna?",
        answer:
          "Ja — kuddar, madrasser, sängkläder, hyllplan och lösa delar tar vi med. Du behöver inte sortera ut det separat.",
      },
      {
        question: "Vad kostar bortforsling av möbler?",
        answer:
          "Priset beror på antalet möbler, deras storlek och hur lång tid uppdraget tar. Du får ett pris direkt i vår bokning baserat på vad du anger — inga dolda avgifter. Avfallsavgifter på återvinningsstationen tillkommer separat och ligger oftast runt 400 kr.",
      },
      {
        question: "Kan ni skänka mina möbler till välgörenhet?",
        answer:
          "Ja! Om dina möbler fortfarande är i bra skick kan vi leverera dem till en secondhand-butik eller välgörenhetsorganisation istället för att köra dem till tippen. Ange bara önskad inlämningsplats vid bokning. Transport till återanvändning kan dessutom ge dig rätt till RUT-avdrag (25 %).",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling av möbler?",
        answer:
          "Om dina möbler transporteras till en secondhand-butik, välgörenhetsorganisation eller annan plats för återanvändning har du rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där möblerna körs direkt till återvinningsstation ger inte rätt till RUT-avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Ingår avfallsavgifter i priset?",
        answer:
          "Nej, avfalls- och deponiavgifter ingår inte i priset utan debiteras separat. Avgiften varierar beroende på återvinningsstation samt vikten och typen av möbler, men ligger oftast runt 400 kr. Du ser alltid den totala kostnaden innan vi påbörjar uppdraget.",
      },
      {
        question: "Hur lång tid tar bortforsling av möbler?",
        answer:
          "Det varierar beroende på antal möbler och hur tillgängliga de är. En enkel bortforsling av en soffa och ett par stolar tar vanligtvis 30–45 minuter. Tömning av möblerna i en hel lägenhet kan ta en halv till en hel dag. Våra ungdomar jobbar effektivt och är vana vid tunga lyft.",
      },
      {
        question: "Kan ni hjälpa med möbeltömning av dödsbo?",
        answer:
          "Ja, vi har gedigen erfarenhet av att tömma dödsbon och hela lägenheter på möbler. Våra ungdomar hanterar allt från soffor och sängar till garderober och skåp. Vi sorterar noggrant — möbler i bra skick skänks vidare och resten lämnas på godkänd återvinningsstation.",
      },
      {
        question: "Kan ni forsla bort möbler från övervåning utan hiss?",
        answer:
          "Absolut. Våra ungdomar är vana vid att bära tunga möbler genom trånga trapphus utan hiss. De vet hur man manövrerar soffor, garderober och sängar runt hörn och avsatser utan att skada väggar eller golv. Bortforsling av möbler från övervåning utan hiss är ett av våra vanligaste uppdrag.",
      },
      {
        question: "Återvinner eller skänker ni möbler som forslas bort?",
        answer:
          "Ja, vi sorterar allt vi forslar bort. Möbler i gott skick skänks till organisationer som Myrorna eller Stadsmissionen. Trasiga eller slitna möbler lämnas på godkänd återvinningsstation där trä, metall och textil sorteras och återvinns. Vårt mål vid varje möbelbortforsling är att så lite som möjligt hamnar på deponi.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Forsla bort möbler i 3 enkla steg",
      step2Text:
        "Våra ungdomar monterar isär det som behövs, bär ut möblerna och lastar allt på bilen. Du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling av möbler idag",
      ctaText:
        "Fyll i adressen, beskriv vilka möbler du vill bli av med, och välj en tid som passar. Du ser priset direkt — inga dolda kostnader.",
    },

    en: {
      slug: "furniture-removal",

      meta: {
        title: "Furniture Removal – Fast & Easy Pickup | Ungdomskraft",
        description:
          "Book furniture removal in Stockholm and Gothenburg – sofas, beds, wardrobes and more. We carry, load and dispose responsibly. Fixed price, insured. Book today!",
        h1: "Furniture removal",
      },

      hero: {
        heading: "Help with furniture removal",
        intro:
          "Time to get rid of old furniture? We pick up everything from a single armchair to a whole home full of belongings. Our young workers carry out, disassemble what's needed, and take everything to recycling. You just show us what needs to go.",
      },

      featureCards: {
        sinceText: "Furniture removal since 2014",
        bookYourText: "Book your furniture removal",
        bookDescText:
          "Enter your address, describe which furniture needs to go, and pick a time. You see the price instantly — no waiting for quotes.",
        vehicleTitleText: "The right vehicle for your furniture",
        vehicleDescText:
          "Whether it's a single armchair or an entire household of furniture, we come with the right van. We load, drive and handle everything — you don't lift a finger.",
      },

      itemsSection: {
        heading: "Furniture we remove",
        items: [
          {
            name: "Sofas",
            description: "Sectionals, two-seaters and sofa beds",
            image: "/sofa.webp",
          },
          {
            name: "Chairs",
            description:
              "Armchairs, accent chairs, reading chairs, rocking chairs and dining chairs",
            image: "/armchair.webp",
          },
          {
            name: "Beds",
            description: "Bed frames, mattresses and headboards",
            image: "/bed.webp",
          },
          {
            name: "Tables",
            description:
              "Dining tables, kitchen tables, coffee tables and nightstands",
            image: "/table.webp",
          },
          {
            name: "Storage",
            description: "Dressers, wardrobes and storage cabinets",
            image: "/wardrobe.webp",
          },
          {
            name: "Patio Furniture",
            description: "Garden furniture and outdoor furniture",
            image: "/patio-furniture.webp",
          },
        ],
      },

      seoContent: {
        mainHeading: "Hassle-free furniture removal",
        mainText:
          "Old furniture is some of the hardest stuff to get rid of on your own. It's heavy, bulky, and rarely fits in a normal car. On top of that, wardrobes and beds often need to be disassembled before they even fit through the door. With Ungdomskraft, you can skip all of that.\n\nWe send young workers with the right vehicle straight to your door. They disassemble what's needed, carry everything out through stairwells and tight passages, and load it onto the van. Items in good condition are donated to organisations like Myrorna and Stadsmissionen — the rest goes to an approved recycling station.\n\nIt doesn't matter whether you're replacing a single sofa or clearing out an entire room. Book online, pick a time that suits you, and we'll take care of the rest.",
        preparationHeading: "Preparing for furniture pickup",
        preparationText:
          "You don't need to disassemble anything — our workers handle that on site. It does help, though, if you empty drawers and shelves before we arrive so things move faster. If you can, clear a path between the furniture and the front door. If you live in an apartment, check whether there's a lift and whether the furniture fits inside it. Have pieces you'd like to donate separately? Just let us know and we'll sort them on site.",
        additionalSections: [
          {
            heading:
              "Why book furniture removal with Ungdomskraft?",
            listItems: [
              {
                label: "Simple booking, no hassle –",
                text: "Book your furniture removal in just a few minutes. Enter your address, choose a date and get a price straight away — no waiting for quotes. Need help today? We can be on site within 60 minutes. You can also call us on +46 8-517 082 81.",
              },
              {
                label: "We carry everything out for you –",
                text: "You don't need to lift a finger. Our young workers carry the furniture out from any floor and through any space — narrow stairwells, tight lifts and inner courtyards are no problem.",
              },
              {
                label: "Donated or recycled –",
                text: "Furniture in good condition is donated to organisations like Myrorna and Stadsmissionen. The rest is sorted and delivered to an approved recycling station. You may also be eligible for the Swedish RUT tax deduction when furniture is transported for reuse.",
              },
              {
                label: "Insured and secure –",
                text: "Every job is insured through Trygg-Hansa. Your home and belongings are protected throughout the entire process — from lifting to transport.",
              },
            ],
          },
          {
            heading: "What furniture can we remove?",
            content:
              "We pick up and remove all types of furniture, regardless of size or condition:",
            listItems: [
              {
                label: "Sofas & armchairs –",
                text: "sectionals, sofa beds, chaise longues, recliners and armchairs",
              },
              {
                label: "Beds –",
                text: "bed frames, mattresses, box springs, headboards and bunk beds",
              },
              {
                label: "Tables & chairs –",
                text: "dining tables, coffee tables, desks, chairs and bar stools",
              },
              {
                label: "Storage –",
                text: "bookcases, wardrobes, dressers, chests of drawers, cabinets and shelving units",
              },
              {
                label: "Office furniture –",
                text: "desks, office chairs, filing cabinets, conference tables and reception counters",
              },
              {
                label: "Other furniture –",
                text: "TV stands, shoe racks, mirrors, coat racks and garden furniture",
              },
            ],
            afterContent:
              "We also disassemble furniture that needs taking apart before it can be carried out. Not sure whether we can handle your particular items? Get in touch and we'll help you out.",
          },
          {
            heading:
              "Experienced furniture movers with local knowledge",
            content:
              "Our young workers have solid experience carrying and removing furniture. They know how to manoeuvre a large sofa through a narrow stairwell, how to protect walls and floors during heavy lifts, and how to load efficiently to minimise the number of trips.\n\nWe carry out furniture removals every single day — from lone armchairs to complete home clearances. That experience means we can give you an accurate price at the time of booking and complete the job smoothly and without delays.",
          },
          {
            heading:
              "More than just furniture removal — you're making a difference",
            content:
              "When most people think about getting rid of old furniture, they picture the hassle: renting a van, recruiting reluctant friends, struggling down narrow staircases and spending a Saturday afternoon at the recycling centre. With Ungdomskraft the experience is completely different — and so is the impact.\n\nUngdomskraft was founded with a clear mission: to give young people their first real work experience while delivering services that make everyday life easier for households and businesses across Sweden. Every furniture removal we carry out is staffed by motivated young workers between the ages of 18 and 25 who are building the skills, confidence and references they need to launch a successful career. For many of them, this is the very first line on their CV.\n\nThat matters more than you might think. Youth unemployment remains one of Sweden's most pressing social challenges, and the single biggest barrier young people face is the classic catch-22: you can't get a job without experience, and you can't get experience without a job. Ungdomskraft breaks that cycle. We train every worker in practical skills — customer communication, logistics, safe lifting techniques, responsible waste handling and basic disassembly — and pair them with experienced team leads who ensure quality on every job.\n\nThe result? You get a professional, fully insured furniture removal carried out by a crew that is genuinely enthusiastic about doing a great job. And every krona you spend goes straight back into employing more young people and expanding our social impact.\n\nBut the positive ripple effects don't stop at employment. Sustainability is woven into every part of how we work. When our team arrives at your home, they don't just load up and drive to the nearest dump. They assess every single piece of furniture. Sofas, tables and wardrobes that are still in usable condition are delivered to charity partners like Myrorna and Stadsmissionen, where they find new homes and stay out of landfill. Items that can't be reused are taken to approved recycling stations and sorted by material — wood, metal, fabric, foam — so that as much as possible re-enters the circular economy. The goal is simple: zero unnecessary waste.\n\nThis approach also benefits you financially. When furniture is transported for reuse rather than disposal, the labour cost may qualify for the Swedish RUT tax deduction, giving you a 50 percent reduction on up to SEK 75,000 per year. We handle the paperwork and apply the deduction directly on the invoice so you never have to front the money.\n\nFor businesses, our furniture removal service doubles as a way to strengthen your corporate social responsibility profile. Hiring Ungdomskraft means you actively support youth employment and sustainable waste management — two areas that resonate strongly with employees, customers and stakeholders alike. We provide documentation of every job, including where items were donated or recycled, so you can report the impact with confidence.\n\nOur young workers bring more than muscle to the job. They bring energy, curiosity and a genuine desire to do things right. Many of them go on to careers in logistics, customer service, construction and environmental management — equipped with references and real-world skills they gained right here. When you book furniture removal with Ungdomskraft, you are not just clearing space in your home. You are investing in a young person's future, supporting a more sustainable way of handling waste, and choosing a company that proves business and social good can go hand in hand.\n\nIt's furniture removal with purpose.",
          },
        ],
      },

      faq: [
        {
          question:
            "What types of furniture can you remove?",
          answer:
            "We remove virtually any type of household or office furniture — sofas, beds, wardrobes, tables, chairs, dressers, bookshelves, desks and more. Whether it's a single piece or an entire home full of furniture, our young workers handle it all. We also take associated items like mattresses, cushions and shelf inserts.",
        },
        {
          question:
            "Can you carry furniture out from any floor?",
          answer:
            "Yes. Our workers are experienced with heavy lifts in tight stairwells, narrow lifts and hard-to-reach spaces. We handle sofas, wardrobes and beds regardless of which floor you're on — you don't need to carry anything out yourself.",
        },
        {
          question:
            "Do you disassemble furniture before removal?",
          answer:
            "Yes, our workers disassemble what's needed — wardrobes, bed frames, desks and similar items. They carry standard tools. If your furniture requires special tools or assembly instructions, please have them ready. Time for disassembly is included in your total work time.",
        },
        {
          question:
            "What happens to the furniture after removal?",
          answer:
            "Furniture in good condition is donated to organisations like Myrorna and Stadsmissionen. Damaged or worn-out pieces are taken to an approved recycling station where materials — wood, metal, fabric — are sorted and recycled. Our goal with every furniture removal is to send as little as possible to landfill.",
        },
        {
          question:
            "Can you donate my furniture to charity?",
          answer:
            "Absolutely. If your furniture is still in decent shape, we can deliver it to a secondhand shop or charity organisation instead of the recycling station. Just let us know your preferred drop-off location when booking. Transporting items for reuse may also qualify you for the Swedish RUT tax deduction (25 %).",
        },
        {
          question:
            "How should I prepare for furniture removal?",
          answer:
            "You don't need to disassemble anything — our workers handle that. It helps if you empty drawers and shelves beforehand, and clear a path between the furniture and the front door. If you live in an apartment, check whether there's a lift and whether the furniture fits inside it.",
        },
        {
          question:
            "Do you offer same-day furniture removal?",
          answer:
            "Yes, we can often be on site the same day. You can book with as little as 30 minutes' notice or plan up to 30 days ahead. Simply choose the time that works best for you — available slots are shown directly at booking.",
        },
        {
          question: "How much does furniture removal cost?",
          answer:
            "The price depends on the number of pieces, their size and how long the job takes. You get an instant price in our booking system based on what you enter — no hidden fees. Disposal fees at the recycling station are charged separately and typically run around SEK 400.",
        },
        {
          question:
            "Can I get a RUT tax deduction on furniture removal?",
          answer:
            "If your furniture is transported to a secondhand shop, charity or another location for reuse, you're eligible for the RUT tax deduction on the labour cost. Standard furniture removal where items go directly to a recycling station does not qualify. The deduction is applied directly on the invoice.",
        },
        {
          question:
            "Are disposal fees included in the furniture removal price?",
          answer:
            "No, waste and disposal fees are not included and are charged separately. The fee varies depending on the recycling station and the weight and type of furniture, but usually comes to around SEK 400. You always see the total cost before we start the job.",
        },
        {
          question: "How long does furniture removal take?",
          answer:
            "It depends on the number of pieces and how accessible they are. Removing a sofa and a couple of chairs typically takes 30–45 minutes. Clearing all the furniture in an apartment can take half a day to a full day. Our young workers are efficient and well used to heavy lifting.",
        },
        {
          question:
            "Can you help with furniture removal from an estate?",
          answer:
            "Yes, we have extensive experience clearing estates and entire apartments of furniture. Our workers handle everything from sofas and beds to wardrobes and cabinets. We sort carefully — furniture in good condition is donated and the rest is taken to an approved recycling station.",
        },
        {
          question:
            "Can you remove furniture from an upper floor without a lift?",
          answer:
            "Absolutely. Our workers are used to carrying heavy furniture through narrow stairwells without a lift. They know how to manoeuvre sofas, wardrobes and beds around corners and landings without damaging walls or floors. Furniture removal from upper floors without a lift is one of our most common jobs.",
        },
        {
          question:
            "Which areas do you offer furniture removal in?",
          answer:
            "We offer furniture removal across the entire Stockholm area: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,

      pricingRows: getPackagePricingRows("bortforsling", "en"),

      dynamicHeadings: {
        threeSteps: "Furniture removal in 3 easy steps",
        step2Text:
          "Our workers disassemble what's needed, carry out all furniture and load the van. You don't need to lift a finger.",
        ctaHeading: "Book furniture removal today",
        ctaText:
          "Enter your address, describe which furniture needs to go, and pick a time. You see the price instantly — no hidden costs.",
      },
    },
  },

  {
    // Bortforsling vitvaror — config för /bortforsling-vitvaror
    // Vinkel: Alla typer av vitvaror — kylskåp, tvättmaskiner, spisar, diskmaskiner, AC.
    // Unik vinkel vs möbler: frikoppling av vatten/el, köldmedier, elavfall (WEEE).
    slug: "bortforsling-vitvaror",
    serviceCategory: "bortforsling",

    meta: {
      title: "Bortforsling vitvaror | Ungdomskraft",
      description:
        "Boka bortforsling av vitvaror – kylskåp, tvättmaskin, spis och frys. Vi kopplar loss, bär ut och kör bort. Fast pris, försäkrat. Stockholm och Göteborg.",
      h1: "Bortforsling vitvaror",
    },

    hero: {
      heading: "Bortforsling av vitvaror",
      intro:
        "Behöver du bli av med gamla vitvaror? Vi hämtar kylskåp, tvättmaskiner, spisar, diskmaskiner och frysar direkt från din bostad. Våra ungdomar kopplar loss, bär ut och kör allt till godkänd återvinning — du behöver inte lyfta ett finger.",
      heroImage: "/hero-bortforsling-vitvaror.png",
    },

    itemsSection: {
      heading: "Vitvaror vi hämtar",
      items: [
        {
          name: "Kylskåp & frysar",
          description: "Kylskåp och frysar",
          image: "/fridge.webp",
        },
        {
          name: "Tvättmaskiner & torktumlare",
          description: "Tvättmaskiner och torktumlare",
          image: "/washer.webp",
        },
        {
          name: "Ugnar & spisar",
          description: "El- och gasspisar",
          image: "/stove.webp",
        },
        {
          name: "Småapparater",
          description:
            "Mikrovågsugnar, brödrostar, airfryers, kaffemaskiner och andra små vitvaror",
          image: "/microwave.webp",
        },
        {
          name: "Diskmaskiner",
          description: "Diskmaskiner och diskutrustning",
          image: "/dishwasher.webp",
        },
        {
          name: "Luftkonditionering",
          description: "Fönster- och portabla AC-enheter",
          image: "/air-conditioner.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Professionell bortforsling av vitvaror",
      mainText:
        "Vitvaror är bland det mest besvärliga att bli av med på egen hand. De väger ofta 50–100 kilo, kräver frikoppling av vatten och el, och innehåller ämnen som freoner och oljor som måste hanteras miljöriktigt. Att försöka bära ut ett kylskåp genom ett trångt trapphus utan rätt utrustning riskerar både skador på bostad och kropp.\n\nMed Ungdomskraft slipper du allt det. Vi skickar ett team med rätt fordon och utrustning direkt till din adress. Våra ungdomar kopplar loss vitvarorna från vatten och el, bär ut dem genom trapphus och trånga passager, och kör allt till godkänd återvinningsstation där köldmedier, oljor och material hanteras enligt gällande miljölagstiftning.\n\nOavsett om det gäller ett enstaka kylskåp eller alla vitvaror i ett kök som ska renoveras — boka online och välj en tid som passar, så ordnar vi resten.",
      preparationHeading: "Förbered inför hämtning av vitvaror",
      preparationText:
        "Stäng av vitvarorna minst 24 timmar före hämtning om det gäller kylskåp eller frysar — det minskar vikt och förhindrar läckage. Töm innehållet i kylskåp, frysar och diskmaskiner. Du behöver inte koppla loss vatten eller el själv — det gör våra ungdomar på plats. Flytta gärna undan lösa saker som står framför eller bredvid vitvarorna, så går det snabbare. Har du tillgång till servicekranar för vatten? Notera var de sitter så kan vi stänga av snabbt.",
      additionalSections: [
        {
          heading: "Varför boka bortforsling av vitvaror med Ungdomskraft?",
          listItems: [
            {
              label: "Vi kopplar loss allt –",
              text: "Våra ungdomar kopplar loss vatten- och elanslutningar säkert på plats. Du behöver inte förbereda något utöver att tömma och stänga av vitvarorna.",
            },
            {
              label: "Tunga lyft? Inga problem –",
              text: "Vitvaror väger ofta 50–100 kg. Vårt team har erfarenhet och utrustning för att hantera tunga lyft i trånga kök, källare och trapphus utan att skada golv eller väggar.",
            },
            {
              label: "Miljöriktig återvinning –",
              text: "Vitvaror klassas som elavfall och innehåller köldmedier, oljor och andra ämnen som kräver specialhantering. Vi lämnar allt på godkänd återvinningsstation där materialen tas om hand enligt lag.",
            },
            {
              label: "Snabbt och flexibelt –",
              text: "Vi kan ofta vara på plats samma dag. Boka med så kort varsel som 30 minuter eller planera upp till 30 dagar i förväg. Du ser tillgängliga tider och pris direkt vid bokning.",
            },
            {
              label: "Försäkrat genom Trygg-Hansa –",
              text: "Alla uppdrag är försäkrade. Din bostad och dina ägodelar är skyddade under hela processen — från losstagning till transport.",
            },
          ],
        },
        {
          heading: "Vilka vitvaror kan vi forsla bort?",
          content:
            "Vi hämtar och forslar bort alla typer av vitvaror, oavsett storlek, ålder och skick:",
          listItems: [
            {
              label: "Kylskåp & frysar –",
              text: "kylskåp, kyl-fryskombinationer, frysboxar, frysskåp, vinkylar och minikylar",
            },
            {
              label: "Tvättmaskiner & torktumlare –",
              text: "tvättmaskiner, torktumlare, kombinerade tvättorkar och manglar",
            },
            {
              label: "Diskmaskiner –",
              text: "fristående diskmaskiner, inbyggda diskmaskiner och bänkdiskmaskiner",
            },
            {
              label: "Spisar & ugnar –",
              text: "spisar, inbyggnadsugnar, häll, mikrovågsugnar och spisfläktar",
            },
            {
              label: "Luftkonditionering –",
              text: "fönstermonterade AC-enheter, portabla AC-enheter och split-system",
            },
            {
              label: "Övrigt –",
              text: "varmvattenberedare, torkskåp, värmepumpar och andra vitvaror",
            },
          ],
          afterContent:
            "Osäker på om vi kan hämta just din vitvara? Hör av dig så hjälper vi dig — vi har sällan behövt tacka nej.",
        },
        {
          heading: "Så hanteras vitvaror miljöriktigt",
          content:
            "Vitvaror får inte slängas med vanliga sopor. De klassas som elavfall (WEEE) och måste lämnas till godkänd återvinningsanläggning. Kylskåp och frysar innehåller köldmedier (ofta freoner) som bryter ned ozonskiktet om de släpps ut. Tvättmaskiner och diskmaskiner innehåller metaller och elektronik som kan återvinnas.\n\nNär du bokar bortforsling av vitvaror med Ungdomskraft säkerställer vi att allt lämnas på rätt ställe. Köldmedier tappas av separat, metaller och plast sorteras, och elektronikkomponenter tas om hand. Över 90 % av materialet i en vitvara kan återvinnas när den hanteras korrekt.",
        },
        {
          heading: "Bortforsling av vitvaror — du gör dubbel nytta",
          content:
            "Att boka bortforsling av vitvaror hos Ungdomskraft innebär mer än att bli av med en gammal tvättmaskin. Du ger samtidigt unga människor riktig arbetslivserfarenhet — från kundkontakt och logistik till praktiskt arbete och miljöansvar.\n\nVarje uppdrag bemannas av motiverade ungdomar som lär sig hantera tunga lyft, kundmöten och ansvarsfull avfallshantering. Det är vitvarubortforsling som gör skillnad — för dig, för miljön och för nästa generations arbetskraft.",
        },
      ],
    },

    faq: [
      {
        question: "Kopplar ni loss vitvarorna åt mig?",
        answer:
          "Ja, våra ungdomar kopplar loss vatten- och elanslutningar på plats. Du behöver inte göra något själv. Vi stänger av servicekranar och drar ur kontakter innan vi bär ut vitvarorna.",
      },
      {
        question: "Kan ni bära ut vitvaror från källare eller övervåning?",
        answer:
          "Absolut. Vi hanterar vitvaror oavsett var de står — källare, övervåning, vindsutrymmen eller trånga kök utan hiss. Våra ungdomar är vana vid tunga lyft i svåra utrymmen och har utrustning som säckkärra och bärremmar.",
      },
      {
        question: "Måste jag tömma kylskåpet innan ni kommer?",
        answer:
          "Ja, vi ber dig tömma innehållet i kylskåp, frysar och diskmaskiner före hämtning. Stäng helst av kylskåp och frysar 24 timmar i förväg så att de hinner tina ur — det minskar vikten och förhindrar vattenläckage under transporten.",
      },
      {
        question: "Hur lång tid tar bortforsling av vitvaror?",
        answer:
          "En enstaka vitvara tar vanligtvis 20–40 minuter beroende på tillgänglighet och våningsplan. Bortforsling av alla vitvaror i ett kök tar normalt 45–90 minuter. Behöver vi koppla loss vatten och el tillkommer lite extra tid.",
      },
      {
        question: "Vad kostar bortforsling av vitvaror?",
        answer:
          "Priset beror på antal vitvaror, storlek och åtkomlighet. En enstaka vitvara som en diskmaskin passar ofta Extra Liten-paketet (595 kr). Ett kylskåp eller en tvättmaskin hamnar vanligtvis på Small (995 kr). Du får ett exakt pris direkt i vår bokning — inga dolda avgifter. Avfallsavgifter på återvinningsstationen tillkommer separat.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling av vitvaror?",
        answer:
          "Om vitvarorna transporteras till en secondhand-butik eller annan plats för återanvändning har du rätt till RUT-avdrag på arbetskostnaden (25 %). Vitvaror som körs till återvinningsstation (det vanligaste) ger inte rätt till RUT-avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Tar ni även med trasiga vitvaror?",
        answer:
          "Ja, vi hämtar vitvaror oavsett skick — fungerande, defekta eller helt utslitna. Allt lämnas på godkänd återvinningsstation där material och köldmedier hanteras enligt miljölagstiftningen.",
      },
      {
        question: "Hur snabbt kan ni komma och hämta?",
        answer:
          "Vi kan ofta vara på plats samma dag. Du kan boka med så kort varsel som 30 minuter eller planera upp till 30 dagar i förväg. Välj den tid som passar dig — du ser tillgängliga tider direkt vid bokning.",
      },
      {
        question: "Ingår avfallsavgifter i priset?",
        answer:
          "Nej, avfalls- och deponiavgifter ingår inte i priset utan debiteras separat. Avgiften varierar beroende på återvinningsstation och typ av vitvara, men ligger oftast runt 400 kr. Du ser alltid den totala kostnaden innan vi påbörjar uppdraget.",
      },
      {
        question: "Kan ni hämta vitvaror från en renovering?",
        answer:
          "Ja, vi hämtar vitvaror i samband med köksrenoveringar och badrumsrenoveringar. Vi kan ta med oss alla gamla vitvaror på samma uppdrag — spis, kylskåp, diskmaskin, tvättmaskin och mer. Boka ett uppdrag så löser vi allt.",
      },
      {
        question: "Kan ni forsla bort inbyggda vitvaror?",
        answer:
          "Ja, vi hanterar bortforsling av inbyggda vitvaror som ugnar, diskmaskiner och mikrovågsugnar. Våra ungdomar lossar vitvarorna från skåpstommen, kopplar bort vatten och el, och bär ut dem. Vi är vana vid att arbeta i trånga köksytor utan att skada omgivande inredning.",
      },
      {
        question: "Vad händer med gamla vitvaror — återvinns de?",
        answer:
          "Ja, alla vitvaror vi forslar bort lämnas på godkänd återvinningsanläggning. Köldmedier tappas av separat, metaller och plast sorteras, och elektronikkomponenter tas om hand. Över 90 % av materialet i en vitvara kan återvinnas när bortforslingen sker korrekt.",
      },
      {
        question: "Kan ni hämta vitvaror från källare eller garage?",
        answer:
          "Absolut. Bortforsling av vitvaror från källare, garage, förråd och vindar är ett av våra vanligaste uppdrag. Våra ungdomar har utrustning som säckkärra och bärremmar för att hantera tunga vitvaror i trånga och svåråtkomliga utrymmen.",
      },
      {
        question: "Erbjuder ni bortforsling av vitvaror med kort varsel?",
        answer:
          "Ja, vi erbjuder bortforsling av vitvaror med kort varsel — ofta samma dag. Du kan boka med så lite som 30 minuters framförhållning om det är akut, till exempel om en tvättmaskin har gått sönder och blockerar utrymmet. Välj tid direkt i bokningsformuläret.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Forsla bort vitvaror i 3 enkla steg",
      step2Text:
        "Våra ungdomar kopplar loss vatten och el, bär ut vitvarorna och lastar allt på bilen. Du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling av vitvaror idag",
      ctaText:
        "Fyll i adressen, beskriv vilka vitvaror du vill bli av med, och välj en tid som passar. Du ser priset direkt — inga dolda kostnader.",
    },

    en: {
      slug: "appliance-removal",
      meta: {
        title: "Appliance Removal | Ungdomskraft",
        description:
          "Book appliance removal – fridges, washing machines, stoves and freezers. We disconnect, carry out and haul away. Fixed price, insured. Stockholm & Gothenburg.",
        h1: "Appliance removal",
      },
      hero: {
        heading: "Appliance removal",
        intro:
          "Need to get rid of old appliances? We pick up fridges, washing machines, stoves, dishwashers and freezers directly from your home. Our team disconnects, carries out and transports everything to approved recycling — you don't have to lift a finger.",
      },
      itemsSection: {
        heading: "Appliances we collect",
        items: [
          {
            name: "Refrigerators",
            description: "Fridges and freezers",
            image: "/fridge.webp",
          },
          {
            name: "Washers & dryers",
            description: "Washing machines and clothes dryers",
            image: "/washer.webp",
          },
          {
            name: "Ovens & stovetops",
            description: "Electric and gas ranges",
            image: "/stove.webp",
          },
          {
            name: "Countertop appliances",
            description:
              "Microwaves, toasters, air fryers, coffee makers, and other small appliances",
            image: "/microwave.webp",
          },
          {
            name: "Dishwashers",
            description: "Dishwashers and dish cleaning appliances",
            image: "/dishwasher.webp",
          },
          {
            name: "Air conditioners",
            description: "Window and portable AC units",
            image: "/air-conditioner.webp",
          },
        ],
      },
      seoContent: {
        mainHeading: "Professional appliance removal",
        mainText:
          "Old appliances are among the most difficult items to dispose of on your own. They often weigh 50–100 kg, require disconnection from water and electricity, and contain substances like refrigerants and oils that must be handled in an environmentally responsible way. Trying to carry a fridge down a narrow staircase without proper equipment risks damage to both your home and your body.\n\nWith Ungdomskraft, you avoid all of that. We send a team with the right vehicle and equipment directly to your address. Our workers disconnect the appliances from water and electricity, carry them out through hallways and tight passages, and transport everything to an approved recycling facility where refrigerants, oils and materials are handled according to environmental regulations.\n\nWhether it's a single fridge or all the appliances in a kitchen being renovated — book online and choose a time that works for you, and we'll handle the rest.",
        preparationHeading: "Preparing for appliance collection",
        preparationText:
          "Turn off fridges and freezers at least 24 hours before collection — this reduces weight and prevents leaks. Empty the contents of fridges, freezers and dishwashers. You don't need to disconnect water or electricity yourself — our team handles that on site. Move any loose items standing in front of or next to the appliances to speed things up. If you have access to service valves for water, note where they are so we can shut them off quickly.",
        additionalSections: [
          {
            heading: "Why book appliance removal with Ungdomskraft?",
            listItems: [
              {
                label: "We disconnect everything –",
                text: "Our team safely disconnects water and electrical connections on site. You don't need to prepare anything beyond emptying and turning off the appliances.",
              },
              {
                label: "Heavy lifting? No problem –",
                text: "Appliances often weigh 50–100 kg. Our team has the experience and equipment to handle heavy lifting in tight kitchens, basements and stairways without damaging floors or walls.",
              },
              {
                label: "Environmentally responsible recycling –",
                text: "Appliances are classified as electronic waste and contain refrigerants, oils and other substances requiring special handling. We deliver everything to approved recycling facilities where materials are processed according to law.",
              },
              {
                label: "Fast and flexible –",
                text: "We can often be on site the same day. Book with as little as 30 minutes' notice or plan up to 30 days ahead. You see available times and pricing instantly when booking.",
              },
              {
                label: "Insured through Trygg-Hansa –",
                text: "All jobs are insured. Your home and belongings are protected throughout the entire process — from disconnection to transport.",
              },
            ],
          },
          {
            heading: "What appliances can we remove?",
            content:
              "We collect and remove all types of appliances, regardless of size, age or condition:",
            listItems: [
              {
                label: "Fridges & freezers –",
                text: "refrigerators, fridge-freezer combos, chest freezers, upright freezers, wine coolers and mini fridges",
              },
              {
                label: "Washing machines & dryers –",
                text: "washing machines, tumble dryers, combined washer-dryers and mangles",
              },
              {
                label: "Dishwashers –",
                text: "freestanding dishwashers, built-in dishwashers and countertop dishwashers",
              },
              {
                label: "Stoves & ovens –",
                text: "stoves, built-in ovens, hobs, microwaves and range hoods",
              },
              {
                label: "Air conditioners –",
                text: "window-mounted AC units, portable AC units and split systems",
              },
              {
                label: "Other –",
                text: "water heaters, drying cabinets, heat pumps and other household appliances",
              },
            ],
            afterContent:
              "Not sure if we can collect your particular appliance? Get in touch and we'll help — we rarely have to say no.",
          },
          {
            heading: "How appliances are recycled responsibly",
            content:
              "Appliances cannot be thrown away with regular waste. They are classified as electronic waste (WEEE) and must be delivered to approved recycling facilities. Fridges and freezers contain refrigerants (often CFCs) that deplete the ozone layer if released. Washing machines and dishwashers contain metals and electronics that can be recycled.\n\nWhen you book appliance removal with Ungdomskraft, we ensure everything is delivered to the right place. Refrigerants are drained separately, metals and plastics are sorted, and electronic components are processed properly. Over 90% of the material in an appliance can be recycled when handled correctly.",
          },
          {
            heading: "Appliance removal that makes a double impact",
            content:
              "Booking appliance removal with Ungdomskraft means more than getting rid of an old washing machine. You're also giving young people real work experience — from customer service and logistics to practical work and environmental responsibility.\n\nEvery job is staffed by motivated young workers who learn to handle heavy lifting, customer interactions and responsible waste management. It's appliance removal that makes a difference — for you, for the environment, and for the next generation's workforce.",
          },
        ],
      },
      faq: [
        {
          question: "Will you disconnect the appliances for me?",
          answer:
            "Yes, our team disconnects water and electrical connections on site. You don't need to do anything yourself. We turn off service valves and unplug appliances before carrying them out.",
        },
        {
          question:
            "Can you carry appliances from a basement or upper floor?",
          answer:
            "Absolutely. We handle appliances regardless of where they are — basements, upper floors, attics or tight kitchens without lifts. Our team is experienced with heavy lifting in difficult spaces and has equipment like sack trucks and carrying straps.",
        },
        {
          question: "Do I need to empty the fridge before you arrive?",
          answer:
            "Yes, we ask you to empty the contents of fridges, freezers and dishwashers before collection. Ideally, turn off fridges and freezers 24 hours in advance so they can defrost — this reduces weight and prevents water leaks during transport.",
        },
        {
          question: "How long does appliance removal take?",
          answer:
            "A single appliance typically takes 20–40 minutes depending on accessibility and floor level. Removing all appliances from a kitchen normally takes 45–90 minutes. If we need to disconnect water and electricity, add a little extra time.",
        },
        {
          question: "How much does appliance removal cost?",
          answer:
            "The price depends on the number of appliances, their size and accessibility. A single smaller appliance like a dishwasher often fits the Extra Liten package (595 SEK). A fridge or washing machine typically falls under Small (995 SEK). You get an exact price instantly in our booking — no hidden fees. Waste fees at the recycling station are charged separately.",
        },
        {
          question: "Do you also take broken appliances?",
          answer:
            "Yes, we collect appliances regardless of condition — working, defective or completely worn out. Everything is delivered to an approved recycling station where materials and refrigerants are handled according to environmental legislation.",
        },
        {
          question: "How quickly can you come?",
          answer:
            "We can often be on site the same day. You can book with as little as 30 minutes' notice or plan up to 30 days ahead. Choose a time that suits you — you see available slots instantly when booking.",
        },
        {
          question: "Are waste fees included in the price?",
          answer:
            "No, waste and disposal fees are not included in the price and are charged separately. The fee varies depending on the recycling station and type of appliance, but is typically around 400 SEK. You always see the total cost before we start the job.",
        },
        {
          question: "Can you collect appliances during a renovation?",
          answer:
            "Yes, we collect appliances during kitchen and bathroom renovations. We can take all old appliances in a single job — stove, fridge, dishwasher, washing machine and more. Book one appointment and we'll sort everything.",
        },
        {
          question:
            "Is appliance removal safe for my kitchen fittings?",
          answer:
            "Yes, our team takes great care during appliance removal to protect surrounding cabinetry, countertops and flooring. We use protective covers and blankets when carrying heavy items through hallways and doorways. All jobs are insured through Trygg-Hansa, so your property is fully covered.",
        },
        {
          question: "Can you remove built-in appliances?",
          answer:
            "Yes, we handle appliance removal of built-in ovens, dishwashers, microwaves and other integrated units. Our team carefully detaches the appliance from the cabinet frame, disconnects water and electricity, and carries it out without damaging surrounding cabinetry.",
        },
        {
          question:
            "What happens to old appliances — are they recycled?",
          answer:
            "Yes, all appliances we collect are delivered to an approved recycling facility. Refrigerants are drained separately, metals and plastics are sorted, and electronic components are processed properly. Over 90% of the material in an appliance can be recycled when appliance removal is done correctly.",
        },
        {
          question:
            "Can you remove appliances from a basement or garage?",
          answer:
            "Absolutely. Appliance removal from basements, garages, storage units and attics is one of our most common jobs. Our team carries sack trucks and lifting straps to handle heavy appliances in tight and hard-to-reach spaces safely.",
        },
        {
          question:
            "Do you offer appliance removal on short notice?",
          answer:
            "Yes, we offer appliance removal with short notice — often the same day. You can book with as little as 30 minutes' notice if it's urgent, for example if a washing machine has broken down and is blocking the space. Choose a time directly in the booking form.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Remove appliances in 3 easy steps",
        step2Text:
          "Our team disconnects water and electricity, carries out the appliances and loads everything onto the truck. You don't have to lift a finger.",
        ctaHeading: "Book appliance removal today",
        ctaText:
          "Enter your address, describe which appliances you want removed, and choose a time that works. You see the price instantly — no hidden costs.",
      },
    },
  },

  {
    // Bortforsling dödsbo — config för /bortforsling-dodsbo
    // Vinkel: Komplett tömning av dödsbo — känslig situation, helhetslösning.
    // Unik vs möbler/vitvaror: sortering, värdering, emotionell aspekt, hela hem.
    slug: "bortforsling-dodsbo",
    serviceCategory: "bortforsling",

    meta: {
      title: "Bortforsling dödsbo | Ungdomskraft",
      description:
        "Boka bortforsling av dödsbo i Stockholm och Göteborg. Vi tömmer, sorterar och forslar bort möbler, vitvaror och bohag – varsamt och med fast pris. Boka idag.",
      h1: "Bortforsling dödsbo",
    },

    hero: {
      heading: "Bortforsling av dödsbo",
      intro:
        "Vi hjälper dig tömma och forsla bort hela dödsbon — respektfullt och noggrant. Våra ungdomar sorterar, bär ut och kör bort möbler, vitvaror, kläder och allt annat bohag. Saker i gott skick skänks vidare, resten återvinns. Du bestämmer vad som ska sparas.",
      heroImage: "/hero-bortforsling.webp",
    },

    itemsSection: {
      heading: "Vad vi hämtar från dödsbon",
      items: [
        {
          name: "Möbler & bohag",
          description:
            "Soffor, sängar, bord, garderober och övriga möbler",
          image: "/sofa.webp",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, spisar, diskmaskiner och frysar",
          image: "/fridge.webp",
        },
        {
          name: "Elektronik",
          description: "TV-apparater, datorer, skrivare och småelektronik",
          image: "/tv.webp",
        },
        {
          name: "Kläder & textilier",
          description:
            "Kläder, sängkläder, handdukar, mattor och gardiner",
          image: "/bedding.webp",
        },
        {
          name: "Husgeråd & porslin",
          description:
            "Köksredskap, porslin, glas, bestick och prydnadsföremål",
          image: "/dishes.webp",
        },
        {
          name: "Övrigt",
          description:
            "Böcker, papper, verktyg, trädgårdsredskap och personliga tillhörigheter",
          image: "/box.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Smidig och respektfull bortforsling av dödsbo",
      mainText:
        "Att tömma ett dödsbo är en av de tyngsta uppgifterna man kan ställas inför — både praktiskt och känslomässigt. Det handlar om ett helt liv i möbler, kläder, minnen och vardagssaker som alla behöver sorteras, hanteras och forslas bort. Ofta under tidspress på grund av uppsägning av hyreskontrakt eller kommande försäljning.\n\nMed Ungdomskraft får du en helhetslösning. Vi tömmer hela bostaden — från tunga möbler och vitvaror till småsaker och personliga tillhörigheter. Våra ungdomar arbetar noggrant och respektfullt. Du bestämmer vad som ska sparas, vad som ska skänkas och vad som ska slängas.\n\nSaker i gott skick skänks vidare till organisationer som Myrorna och Stadsmissionen. Elektronik och vitvaror lämnas till godkänd återvinning. Resten sorteras och körs till avfallsstation. Vi ser till att så lite som möjligt hamnar på deponi.",
      preparationHeading: "Förbered inför tömning av dödsbo",
      preparationText:
        "Gå igenom bostaden och markera eller plocka undan saker du vill behålla — foton, smycken, dokument och andra personliga värdesaker. Du behöver inte sortera resten — det gör vi på plats. Kontrollera om det finns förvaringsutrymmen som vind, källare eller förråd som också ska tömmas, så att vi kan planera rätt bemanning och fordon. Har du nycklar till alla utrymmen? Se till att de finns tillgängliga vid hämtningen.",
      additionalSections: [
        {
          heading: "Varför boka bortforsling av dödsbo med Ungdomskraft?",
          listItems: [
            {
              label: "Helhetslösning –",
              text: "Vi tömmer hela bostaden i ett uppdrag. Möbler, vitvaror, kläder, papper, elektronik och allt annat bohag — du behöver inte boka flera tjänster eller hantera något själv.",
            },
            {
              label: "Respektfullt och noggrant –",
              text: "Vi förstår att ett dödsbo innehåller minnen och personliga tillhörigheter. Våra ungdomar arbetar varsamt och ger dig tid att gå igenom det som är viktigt innan vi börjar.",
            },
            {
              label: "Sortering på plats –",
              text: "Vi sorterar allt i kategorier: behålla, skänka och slänga. Saker i bra skick levereras till secondhand. Farligt avfall, elektronik och vitvaror lämnas på rätt mottagningsställe.",
            },
            {
              label: "Snabbt när det behövs –",
              text: "Har du tidspress på grund av uppsagd hyra eller försäljning? Vi kan ofta vara på plats samma dag och genomföra tömningen snabbt utan att kompromissa med noggrannheten.",
            },
            {
              label: "Försäkrat genom Trygg-Hansa –",
              text: "Alla uppdrag är försäkrade. Bostaden och eventuella saker du vill behålla är skyddade under hela processen.",
            },
          ],
        },
        {
          heading: "Så går en dödsbo-tömning till",
          content:
            "Vi börjar med att gå igenom bostaden tillsammans med dig — antingen på plats eller via bilder. Du pekar ut vad som ska sparas, och vi tar hand om resten.",
          listItems: [
            {
              label: "Steg 1: Genomgång –",
              text: "Vi kartlägger bostadens storlek, antal rum, och hur mycket som ska forslas bort. Du får ett fast pris baserat på uppdraget.",
            },
            {
              label: "Steg 2: Sortering –",
              text: "Våra ungdomar sorterar bohaget i kategorier. Saker du vill behålla ställs åt sidan. Saker i gott skick förbereds för donation.",
            },
            {
              label: "Steg 3: Bortforsling –",
              text: "Vi bär ut allting — möbler, vitvaror, kartonger och lösa saker. Allt lastas och körs till respektive mottagningsställe.",
            },
            {
              label: "Steg 4: Slutresultat –",
              text: "Bostaden lämnas tom och sopfri. Behöver du flyttstädning kan vi hjälpa även med det.",
            },
          ],
        },
        {
          heading: "Erfarna ungdomar med rätt inställning",
          content:
            "Bortforsling av dödsbo kräver mer än fysisk styrka — det kräver empati och omdöme. Våra ungdomar är utbildade i att hantera känsliga uppdrag med respekt. De arbetar systematiskt genom rum för rum, sorterar noggrant och ställer frågor om de hittar saker som kan vara värdefulla.\n\nVi utför regelbundet dödsbo-tömningar i hela Stockholmsregionen och har erfarenhet av allt från enstaka rum till stora villor. Den erfarenheten gör att vi kan planera effektivt, ge dig ett exakt pris och genomföra uppdraget utan fördröjningar.",
        },
        {
          heading: "Mer än bortforsling — du gör skillnad",
          content:
            "När du bokar bortforsling av dödsbo med Ungdomskraft bidrar du till att unga människor får arbetslivserfarenhet. Varje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundkontakt och logistik till ansvarsfullt miljöarbete.\n\nSamtidigt ser vi till att så mycket som möjligt av bohaget får nytt liv genom secondhand och återvinning istället för att hamna på soptippen. Det är dödsbo-tömning som gör skillnad — för dig, för miljön och för nästa generation.",
        },
      ],
    },

    faq: [
      {
        question: "Hur lång tid tar tömning av ett dödsbo?",
        answer:
          "Det beror på bostadens storlek och hur mycket som ska forslas bort. En enrumslägenhet tar normalt en halv dag, en trea runt en hel dag. Större villor kan ta 1–2 dagar. Vi ger dig en uppskattning vid bokning baserat på vad du beskriver.",
      },
      {
        question: "Vad kostar bortforsling av dödsbo?",
        answer:
          "Priset baseras på bostadens storlek, mängden bohag och hur lång tid uppdraget tar. Du får ett prisförslag direkt vid bokning. En enrumslägenhet börjar typiskt runt 3 000–5 000 kr, medan en större bostad kan kosta 8 000–15 000 kr. Avfallsavgifter tillkommer separat.",
      },
      {
        question: "Kan vi behålla vissa saker?",
        answer:
          "Absolut. Du bestämmer helt vad som ska sparas. Vi rekommenderar att du går igenom bostaden och markerar saker du vill behålla innan vi börjar. Våra ungdomar frågar alltid om de hittar saker som verkar personliga eller värdefulla.",
      },
      {
        question: "Hjälper ni till att skänka saker till välgörenhet?",
        answer:
          "Ja! Möbler, kläder och husgeråd i gott skick skänks vidare till organisationer som Myrorna, Stadsmissionen eller lokala secondhand-butiker. Transport till återanvändning kan dessutom ge dig rätt till RUT-avdrag.",
      },
      {
        question: "Kan jag få RUT-avdrag på tömning av dödsbo?",
        answer:
          "Ja, om bohaget transporteras till en secondhand-butik eller annan plats för återanvändning har du rätt till RUT-avdrag på arbetskostnaden (25 %). Vanlig bortforsling till återvinningsstation ger inte rätt till RUT. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hanterar ni även vind, källare och förråd?",
        answer:
          "Ja, vi tömmer hela bostaden inklusive alla förvaringsutrymmen — vind, källare, garage och förråd. Ange alla utrymmen vid bokning så planerar vi rätt bemanning och fordon.",
      },
      {
        question: "Hur snabbt kan ni komma?",
        answer:
          "Vi kan ofta vara på plats inom ett par dagar. Vid akuta behov — till exempel uppsagt hyreskontrakt — kan vi ibland genomföra tömningen redan nästa dag. Kontakta oss så hittar vi en lösning.",
      },
      {
        question: "Ingår städning efter tömningen?",
        answer:
          "Grundläggande utrymning ingår — vi lämnar bostaden tom och sopfri. Fullständig flyttstädning kan bokas som tilläggstjänst. Hör av dig så ger vi ett pris för båda delarna.",
      },
      {
        question: "Vad händer med farligt avfall?",
        answer:
          "Färg, lösningsmedel, kemikalier och andra miljöfarliga ämnen sorteras separat och lämnas på godkänd miljöstation. Elektronik och vitvaror med köldmedier lämnas på särskild mottagning. Vi ser till att allt hanteras enligt gällande miljöregler.",
      },
      {
        question: "Kan ni hjälpa om vi bor på annan ort?",
        answer:
          "Ja, det är vanligt att anhöriga bor på annan ort. Du kan boka och beskriva uppdraget online, skicka bilder på bostaden, och lämna nycklar till en granne eller mäklare. Vi sköter resten och rapporterar tillbaka när allt är klart.",
      },
      {
        question: "Hanterar ni bortforsling av dödsbo med känslomässigt värde?",
        answer:
          "Ja, vi förstår att dödsbon ofta innehåller föremål med stort känslomässigt värde. Våra ungdomar arbetar varsamt och frågar alltid innan de forslar bort saker som verkar personliga — fotoalbum, brev, smycken och liknande. Du bestämmer i förväg vad som ska sparas, och vi ser till att inget värdefullt hamnar fel vid dödsbobortforslingen.",
      },
      {
        question: "Samarbetar ni med boutredningsmän eller jurister vid bortforsling av dödsbo?",
        answer:
          "Ja, vi samarbetar regelbundet med boutredningsmän, jurister och mäklare i samband med bortforsling av dödsbo. Vi kan koordinera tömningen med deras tidsplan och ge dem direkt tillgång till nyckelkvitton och dokumentation. Det gör processen smidigare för alla inblandade.",
      },
      {
        question: "Kan ni göra en delvis bortforsling av dödsbo?",
        answer:
          "Ja, du behöver inte tömma hela dödsboet på en gång. Vi kan forsla bort enstaka rum, specifika möbelgrupper eller enbart vitvaror och elektronik. Beskriv vad som ska bort vid bokning så anpassar vi bemanning och fordon efter just ditt behov.",
      },
      {
        question: "Skänker ni användbara saker vidare vid dödsbobortforsling?",
        answer:
          "Ja, det är en viktig del av vår dödsbobortforsling. Möbler, kläder och husgeråd i gott skick levereras till organisationer som Myrorna och Stadsmissionen. Transport till återanvändning kan dessutom ge dig rätt till RUT-avdrag (25 %). Vi sorterar noggrant på plats så att så mycket som möjligt får nytt liv.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Töm dödsbo i 3 enkla steg",
      step2Text:
        "Våra ungdomar sorterar bohaget, bär ut allt och lastar på bilen. Du bestämmer vad som ska sparas — vi tar hand om resten.",
      ctaHeading: "Boka bortforsling av dödsbo idag",
      ctaText:
        "Fyll i adressen, beskriv bostaden och vad som ska tömmas, och välj en tid som passar. Du får ett prisförslag direkt — inga dolda kostnader.",
    },

    en: {
      slug: "estate-clearance",
      meta: {
        title: "Estate Clearance | Ungdomskraft",
        description:
          "Book estate clearance in Stockholm and Gothenburg. We clear, sort and remove furniture, appliances and belongings – carefully and at a fixed price. Book today.",
        h1: "Estate clearance",
      },
      hero: {
        heading: "Estate clearance",
        intro:
          "We help you clear entire estates — respectfully and thoroughly. Our team sorts, carries out and removes furniture, appliances, clothes and all other household items. Items in good condition are donated, the rest is recycled. You decide what to keep.",
      },
      itemsSection: {
        heading: "What we collect from estates",
        items: [
          {
            name: "Furniture & household",
            description:
              "Sofas, beds, tables, wardrobes and other furniture",
            image: "/sofa.webp",
          },
          {
            name: "Appliances",
            description:
              "Fridges, washing machines, stoves, dishwashers and freezers",
            image: "/fridge.webp",
          },
          {
            name: "Electronics",
            description: "TVs, computers, printers and small electronics",
            image: "/tv.webp",
          },
          {
            name: "Clothes & textiles",
            description:
              "Clothing, bedding, towels, rugs and curtains",
            image: "/bedding.webp",
          },
          {
            name: "Kitchenware & china",
            description:
              "Kitchen utensils, porcelain, glassware, cutlery and ornaments",
            image: "/dishes.webp",
          },
          {
            name: "Other",
            description:
              "Books, papers, tools, garden equipment and personal belongings",
            image: "/box.webp",
          },
        ],
      },
      seoContent: {
        mainHeading: "Smooth and respectful estate clearance",
        mainText:
          "Clearing a deceased estate is one of the most demanding tasks you can face — both practically and emotionally. It involves an entire lifetime of furniture, clothes, memories and everyday items that all need to be sorted, handled and removed. Often under time pressure due to tenancy termination or an upcoming property sale.\n\nWith Ungdomskraft, you get a complete solution. We clear the entire home — from heavy furniture and appliances to small items and personal belongings. Our team works carefully and respectfully. You decide what to keep, what to donate and what to discard.\n\nItems in good condition are donated to charity organisations. Electronics and appliances are taken to approved recycling. The rest is sorted and transported to waste facilities. We make sure as little as possible ends up in landfill.",
        preparationHeading: "Preparing for estate clearance",
        preparationText:
          "Go through the home and set aside or mark items you want to keep — photos, jewellery, documents and other personal valuables. You don't need to sort the rest — we do that on site. Check if there are storage spaces such as attic, basement or storage units that also need clearing, so we can plan the right crew and vehicle. Do you have keys to all spaces? Make sure they're available on the day of collection.",
        additionalSections: [
          {
            heading: "Why book estate clearance with Ungdomskraft?",
            listItems: [
              {
                label: "Complete solution –",
                text: "We clear the entire home in one job. Furniture, appliances, clothes, paperwork, electronics and all other household items — you don't need to book multiple services or handle anything yourself.",
              },
              {
                label: "Respectful and thorough –",
                text: "We understand that an estate contains memories and personal belongings. Our team works carefully and gives you time to go through what matters before we start.",
              },
              {
                label: "On-site sorting –",
                text: "We sort everything into categories: keep, donate and dispose. Items in good condition are delivered to charity shops. Hazardous waste, electronics and appliances are taken to the right facilities.",
              },
              {
                label: "Fast when needed –",
                text: "Under time pressure due to a terminated lease or property sale? We can often be on site the same day and complete the clearance quickly without compromising on thoroughness.",
              },
              {
                label: "Insured through Trygg-Hansa –",
                text: "All jobs are insured. The property and any items you want to keep are protected throughout the entire process.",
              },
            ],
          },
          {
            heading: "How estate clearance works",
            content:
              "We start by reviewing the property together with you — either on site or via photos. You point out what to keep, and we take care of the rest.",
            listItems: [
              {
                label: "Step 1: Assessment –",
                text: "We map out the property size, number of rooms, and how much needs to be removed. You get a fixed price based on the job.",
              },
              {
                label: "Step 2: Sorting –",
                text: "Our team sorts the household items into categories. Items you want to keep are set aside. Items in good condition are prepared for donation.",
              },
              {
                label: "Step 3: Removal –",
                text: "We carry everything out — furniture, appliances, boxes and loose items. Everything is loaded and taken to the appropriate facility.",
              },
              {
                label: "Step 4: Result –",
                text: "The property is left empty and clean. If you need a full move-out clean, we can help with that too.",
              },
            ],
          },
          {
            heading: "Experienced team with the right approach",
            content:
              "Estate clearance requires more than physical strength — it requires empathy and judgement. Our young workers are trained to handle sensitive jobs with respect. They work systematically room by room, sort carefully and ask questions if they find items that might be valuable.\n\nWe regularly carry out estate clearances across the greater Stockholm area and have experience with everything from single rooms to large houses. That experience means we can plan efficiently, give you an accurate price and complete the job without delays.",
          },
          {
            heading: "More than clearance — you make a difference",
            content:
              "When you book estate clearance with Ungdomskraft, you give young people real work experience. Every job is staffed by motivated young workers who learn customer service, logistics and responsible waste management.\n\nAt the same time, we ensure that as much of the household as possible gets a second life through charity shops and recycling instead of ending up in landfill. It's estate clearance that makes a difference — for you, for the environment and for the next generation.",
          },
        ],
      },
      faq: [
        {
          question: "How long does estate clearance take?",
          answer:
            "It depends on the size of the property and how much needs removing. A one-bedroom flat typically takes half a day, a two-bedroom around a full day. Larger houses can take 1–2 days. We give you an estimate at booking based on your description.",
        },
        {
          question: "How much does estate clearance cost?",
          answer:
            "The price is based on property size, amount of belongings and how long the job takes. You get a price estimate directly when booking. A one-bedroom flat typically starts around 3,000–5,000 SEK, while a larger property may cost 8,000–15,000 SEK. Waste fees are charged separately.",
        },
        {
          question: "Can we keep certain items?",
          answer:
            "Absolutely. You decide what to keep. We recommend going through the property and marking items you want to save before we start. Our team always asks if they find items that appear personal or valuable.",
        },
        {
          question: "Do you help donate items to charity?",
          answer:
            "Yes! Furniture, clothes and kitchenware in good condition are donated to organisations like charity shops and local secondhand stores. Transport to reuse can also qualify you for a RUT tax deduction.",
        },
        {
          question: "Do you also clear attics, basements and storage?",
          answer:
            "Yes, we clear the entire property including all storage spaces — attic, basement, garage and storage units. Mention all spaces when booking so we can plan the right crew and vehicle.",
        },
        {
          question: "How quickly can you come?",
          answer:
            "We can often be on site within a couple of days. For urgent needs — for example a terminated lease — we can sometimes carry out the clearance the next day. Contact us and we'll find a solution.",
        },
        {
          question: "Is cleaning included after the clearance?",
          answer:
            "Basic clearance is included — we leave the property empty and free of debris. A full move-out clean can be booked as an add-on. Get in touch and we'll quote both services together.",
        },
        {
          question: "What happens to hazardous waste?",
          answer:
            "Paint, solvents, chemicals and other hazardous materials are sorted separately and delivered to an approved environmental station. Electronics and appliances with refrigerants go to specialist recycling. We ensure everything is handled according to environmental regulations.",
        },
        {
          question: "Can you help if we live in another city?",
          answer:
            "Yes, it's common for relatives to live elsewhere. You can book and describe the job online, send photos of the property, and leave keys with a neighbour or estate agent. We handle the rest and report back when everything is done.",
        },
        {
          question:
            "Can you handle estate clearance with sentimental items?",
          answer:
            "Yes, we understand that estates often contain items of great sentimental value. Our team works carefully and always asks before removing things that appear personal — photo albums, letters, jewellery and similar keepsakes. You decide in advance what to keep, and we make sure nothing valuable is lost during the estate clearance.",
        },
        {
          question:
            "Do you work with estate administrators or lawyers?",
          answer:
            "Yes, we regularly coordinate estate clearance with estate administrators, lawyers and real-estate agents. We can align the clearance with their timeline and provide them with key receipts and documentation. This makes the entire estate removal process smoother for everyone involved.",
        },
        {
          question:
            "How far in advance should I book estate clearance?",
          answer:
            "We recommend booking estate clearance at least a few days ahead so we can allocate the right crew and vehicle. However, if you're under time pressure — for example due to a terminated lease — we can often arrange a clearance within 24–48 hours. Contact us and we'll find a slot that works.",
        },
        {
          question: "Can you do a partial estate clearance?",
          answer:
            "Yes, you don't have to clear the entire estate at once. We can remove individual rooms, specific furniture groups, or only appliances and electronics. Describe what needs to go when booking and we'll adjust crew and vehicle to match your needs.",
        },
        {
          question:
            "Do you donate usable items from estate clearances?",
          answer:
            "Yes, donating usable items is an important part of our estate clearance service. Furniture, clothes and kitchenware in good condition are delivered to charity organisations and local secondhand shops. We sort carefully on site so that as much as possible gets a second life instead of going to landfill.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Clear an estate in 3 easy steps",
        step2Text:
          "Our team sorts the belongings, carries everything out and loads it onto the truck. You decide what to keep — we handle the rest.",
        ctaHeading: "Book estate clearance today",
        ctaText:
          "Enter your address, describe the property and what needs clearing, and choose a time that works. You get a price estimate instantly — no hidden costs.",
      },
    },
  },
];
